import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  EstateInputDataFormatListOption,
  EstateInputDataFormatListOption_CreateEstateInputDataFormatListOptionInput,
  EstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdDocument,
  EstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionInput,
  useEstateInputDataFormatListOption_CreateEstateInputDataFormatListOptionMutation,
  useEstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionMutation,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  estateInputDataFormatId: string;
  isEditing: boolean;
  selectedEstateInputDataFormatListOption?: EstateInputDataFormatListOption | undefined;
  onExitClick: () => void;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    textAlign: 'left',
  },
  submitButton: {
    paddingTop: '10px',
  },
}));

type EstateInputDataFormatListOptionFormValues = {
  label: string;
  value: string;
};

const requiredTextFields = ['label', 'value'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});

const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

export default function EstateInputDataFormatListOptionForm({
  estateInputDataFormatId,
  isEditing,
  selectedEstateInputDataFormatListOption,
  onExitClick,
}: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = useForm<EstateInputDataFormatListOptionFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      label: selectedEstateInputDataFormatListOption?.label || '',
      value: selectedEstateInputDataFormatListOption?.value || undefined,
    },
  });

  const [createEstateInputDataFormatListOption] =
    useEstateInputDataFormatListOption_CreateEstateInputDataFormatListOptionMutation({
      refetchQueries: [
        {
          query:
            EstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdDocument,
          variables: { input: { estateInputDataFormatId } },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Estate input data format list options created.',
          severity: NotificationSeverity.SUCCESS,
        });
        onExitClick();
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating estate input data format list options: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateEstateInputDataFormatListOption] =
    useEstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionMutation({
      refetchQueries: [
        {
          query:
            EstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdDocument,
          variables: { input: { estateInputDataFormatId } },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Estate input data format list options updated.',
          severity: NotificationSeverity.SUCCESS,
        });
        onExitClick();
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating estate input data format  list options: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const submitForm = async ({ ...formData }: EstateInputDataFormatListOptionFormValues) => {
    if (!formData.label || !formData.value) {
      showAlert({
        message: 'Missing field(s) required',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (selectedEstateInputDataFormatListOption) {
      const input: EstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionInput = {
        ...formData,
        id: selectedEstateInputDataFormatListOption.id,
      };

      updateEstateInputDataFormatListOption({
        variables: {
          input,
        },
      });
    } else {
      const input: EstateInputDataFormatListOption_CreateEstateInputDataFormatListOptionInput = {
        ...formData,
        estateInputDataFormatId,
      };

      createEstateInputDataFormatListOption({
        variables: {
          input,
        },
      });
    }
  };

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        {isEditing ? 'Edit' : 'Create'} Estate Input Data Format List Option
      </Typography>
      <form
        id="estate-input-data-format-list-option-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="label"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    autoFocus
                    inputProps={{ maxLength: 80 }}
                    label="Label"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="value"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    inputProps={{ maxLength: 80 }}
                    label="Value"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={clsx(classes.container, classes.submitButton)}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="estate-input-data-format-list-option-form"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
